import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import GenericTabNavigation from '../components/GenericTabNavigation';
import AdminTranslationsTab from '../components/admin/AdminTranslationsTab';
import AdminIaModelsTab from '../components/admin/AdminIaModelsTab';
import AdminLanguagesTab from '../components/admin/AdminLanguagesTab';
import AdminManuscriptTagsTab from '../components/admin/AdminManuscriptTagsTab';
import AdminTabActionsTab from '../components/admin/AdminTabActionsTab';
import AdminManuscript2ArticleTab from '../components/admin/AdminManuscript2ArticleTab';
import AdminIaCallsTab from '../components/admin/AdminIaCallsTab';
import AdminArticleRagActionsTab from '../components/admin/AdminArticleRagActionsTab';
import AdminArticleFormatsTab from '../components/admin/AdminArticleFormatsTab';
import AdminFixedTranslationsTab from '../components/admin/AdminFixedTranslationsTab';
import AdminHandlebarsTab from '../components/admin/AdminHandlebarsTab';
import ErrorModal from '../components/ErrorModal';
import { checkIsMaster } from '../utils/config-calls';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';

/**
 * Page d'administration des données de configuration
 * Nécessite d'être authentifié avec une clé MASTER
 */
export default function AdminToolsPage() {
  const { t, loadPageTranslations } = useTranslation();
  const { isAuthenticated, user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('translations');
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);
  const authContext = useContext(AuthContext);
  const wa_world = authContext?.wa_world;

  // Charger les traductions pour la page ADMIN
  useEffect(() => {
    loadPageTranslations('ADMIN');
  }, [loadPageTranslations]);

  // Vérifier si l'utilisateur est authentifié et admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      try {
        setChecking(true);
        const result = await checkIsMaster();
        
        if (result.success && result.isMaster) {
          setIsAdmin(true);
          debug.log(config, 'Accès administrateur confirmé');
        } else {
          setIsAdmin(false);
          setError({
            title: t('ADMIN', 'ACCESS_DENIED', 'Accès refusé'),
            message: t('ADMIN', 'NEED_MASTER_KEY', 'Vous devez disposer de la clé MASTER pour accéder à cette section.'),
          });
          debug.warn(config, 'Accès administrateur refusé');
        }
      } catch (err) {
        setIsAdmin(false);
        setError({
          title: t('ADMIN', 'ACCESS_ERROR', 'Erreur de vérification'),
          message: t('ADMIN', 'CHECK_FAILED', 'Impossible de vérifier vos droits d\'accès.'),
        });
        debug.error(config, 'Erreur lors de la vérification des droits admin', err);
      } finally {
        setChecking(false);
      }
    };

    checkAdminStatus();
  }, [isAuthenticated, navigate, t]);

  // Onglets disponibles
  const tabs = [
    { id: 'translations', label: t('ADMIN', 'TAB_TRANSLATIONS', 'Traductions') },
    { id: 'iamodels', label: t('ADMIN', 'TAB_IAMODELS', 'Modèles IA') },
    { id: 'iacalls', label: t('ADMIN', 'TAB_IACALLS', 'Appels IA') },
    { id: 'articleragactions', label: t('ADMIN', 'TAB_ARTICLERAGACTIONS', 'Templates RAG') },
    { id: 'languages', label: t('ADMIN', 'TAB_LANGUAGES', 'Langues') },
    { id: 'manuscripttags', label: t('ADMIN', 'TAB_MANUSCRIPTTAGS', 'Tags des manuscrits') },
    { id: 'tabactions', label: t('ADMIN', 'TAB_TABACTIONS', 'Table des actions') },
    { id: 'manuscript2article', label: t('ADMIN', 'TAB_MANUSCRIPT2ARTICLE', 'Manuscript2Article') },
    { id: 'articleformats', label: t('ADMIN', 'TAB_ARTICLEFORMATS', 'Formats d\'articles') },
    { id: 'fixedtranslations', label: t('ADMIN', 'TAB_FIXEDTRANSLATIONS', 'Traductions fixes') },
    { id: 'handlebars', label: t('ADMIN', 'TAB_HANDLEBARS', 'Templates Handlebars') },
  ];

  // Fermer la modal d'erreur et rediriger vers la page d'accueil
  const handleErrorClose = () => {
    setError(null);
    navigate('/');
  };

  // Afficher l'interface de chargement ou d'erreur si nécessaire
  if (checking) {
    return (
      <div className="wa-admin-page loading">
        <h1>{t('ADMIN', 'CHECKING_ACCESS', 'Vérification des droits d\'accès...')}</h1>
        <div className="wa-loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="wa-admin-page">
      <h1>{t('ADMIN', 'TOOLS_TITLE', 'Outils d\'administration')}</h1>
      
      {isAdmin ? (
        <>
          <GenericTabNavigation 
            tabs={tabs} 
            activeTab={activeTab} 
            onTabChange={setActiveTab} 
          >
            <div className="wa-admin-content">
              {activeTab === 'translations' && <AdminTranslationsTab />}
              {activeTab === 'iamodels' && <AdminIaModelsTab />}
              {activeTab === 'iacalls' && <AdminIaCallsTab />}
              {activeTab === 'articleragactions' && <AdminArticleRagActionsTab />}
              {activeTab === 'languages' && <AdminLanguagesTab />}
              {activeTab === 'manuscripttags' && <AdminManuscriptTagsTab />}
              {activeTab === 'tabactions' && <AdminTabActionsTab />}
              {activeTab === 'manuscript2article' && <AdminManuscript2ArticleTab />}
              {activeTab === 'articleformats' && <AdminArticleFormatsTab />}
              {activeTab === 'fixedtranslations' && <AdminFixedTranslationsTab />}
              {activeTab === 'handlebars' && <AdminHandlebarsTab />}
            </div>
          </GenericTabNavigation>
        </>
      ) : (
        <div className="wa-admin-access-denied">
          <h2>{t('ADMIN', 'ACCESS_DENIED', 'Accès refusé')}</h2>
          <p>{t('ADMIN', 'NEED_MASTER_KEY', 'Vous devez disposer de la clé MASTER pour accéder à cette section.')}</p>
        </div>
      )}

      {error && (
        <ErrorModal
          title={error.title}
          message={error.message}
          onClose={handleErrorClose}
        />
      )}
    </div>
  );
}
